import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Briefcase, Clock, DollarSign, Tag, ArrowLeft,
  MapPin, User, CheckCircle, History, Info,
  Scissors, Settings, Layers, Star, TrendingUp
} from "lucide-react";
import { getBusinessServiceById } from "../../../services/businessServicesApi";
import { BusinessService } from "../../../types/businessService";
import GlobalLoader from "../../../components/ui/GlobalLoader";
import PageHeader from "../../../components/ui/PageHeader";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";

export default function ServiceDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [service, setService] = useState<BusinessService | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await getBusinessServiceById(id);
        if (data) {
          setService(data);
        } else {
          setError("Service not found.");
        }
      } catch (err) {
        console.error("Failed to load service details", err);
        setError("Could not load service details.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  const chartData = useMemo(() => {
    const months = ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return months.map((m, i) => ({
      name: m,
      value: 10 + Math.floor(Math.random() * 30) + (i * 5)
    }));
  }, []);

  if (loading) {
    return (
      <div className="flex h-[80vh] w-full items-center justify-center bg-gray-50">
        <GlobalLoader description="Loading service details..." />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-50 text-center p-6">
        <Scissors className="w-12 h-12 text-[#885ABB]/50 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Service Not Found</h2>
        <p className="text-gray-500 mb-6">{error || "The requested service details could not be found."}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-[#885ABB] text-white rounded-full hover:bg-[#724b9e] transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <PageHeader
        title="Service Details"
        icon={Briefcase}
        className="border-b-2 border-gray-300 shadow-sm"
        actions={
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">

        {/* Hero Section */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 rounded-full blur-3xl -mr-32 -mt-32 opacity-50"></div>

          <div className="relative flex flex-col md:flex-row items-center md:items-start gap-8">
            <div className="relative">
              <div className="w-32 h-32 rounded-full bg-purple-100 border-4 border-white shadow-md flex items-center justify-center overflow-hidden">
                <Scissors className="w-16 h-16 text-[#885ABB]" />
              </div>
              <div className="absolute bottom-1 right-1 group">
                <div className={`w-6 h-6 border-4 border-white rounded-full ${service.status === 'active' ? 'bg-green-500' : 'bg-gray-300'} transition-colors cursor-help`}></div>

                {/* Status Tooltip */}
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-lg z-10">
                  {service.status === 'active' ? 'Status: Active' : 'Status: Inactive'}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>

            <div className="flex-1 text-center md:text-left space-y-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center md:justify-start gap-3">
                  {service.name}
                  {service.status === 'active' && (
                    <CheckCircle className="w-5 h-5 text-[#885ABB]" fill="currentColor" stroke="white" />
                  )}
                </h1>
                <p className="text-gray-500 flex items-center justify-center md:justify-start gap-2 mt-1 lowercase first-letter:uppercase">
                  <Layers className="w-4 h-4" />
                  {service.categoryName || "Uncategorized"}
                </p>
              </div>

              <div className="flex flex-wrap justify-center md:justify-start gap-2">
                {service.discount !== undefined && service.discount > 0 && (
                  <span className="px-3 py-1 rounded-full bg-red-50 text-red-600 text-[10px] font-bold uppercase border border-red-100 flex items-center gap-1">
                    <Tag className="w-3 h-3" /> {service.discount}% Off
                  </span>
                )}
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full md:w-auto">
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 text-center min-w-[100px]">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Standard Price</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(service.price)}</p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50 border border-purple-100 text-center min-w-[100px]">
                <p className="text-xs font-medium text-[#885ABB] uppercase tracking-wide">Duration</p>
                <p className="text-xl font-bold text-[#885ABB] mt-1">{service.durationLabel}</p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50/70 border border-purple-100/70 text-center min-w-[100px]">
                <p className="text-xs font-medium text-[#885ABB]/80 uppercase tracking-wide flex items-center justify-center gap-1">Staff</p>
                <p className="text-lg font-bold text-[#885ABB] mt-1">{service.staff?.length || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50/40 border border-purple-100/40 text-center min-w-[100px]">
                <p className="text-xs font-medium text-[#885ABB]/60 uppercase tracking-wide">Status</p>
                <p className="text-lg font-bold text-[#885ABB] mt-1 uppercase">{service.status}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left Column */}
          <div className="space-y-6">
            {/* Trend Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden p-6">
              <h3 className="font-semibold text-[#885ABB] mb-6 flex items-center gap-2">
                <TrendingUp size={18} /> Booking Trends
              </h3>
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#885ABB" stopOpacity={0.1} />
                        <stop offset="95%" stopColor="#885ABB" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                    <YAxis hide />
                    <RechartsTooltip />
                    <Area type="monotone" dataKey="value" stroke="#885ABB" fillOpacity={1} fill="url(#colorValue)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b-2 border-purple-200 bg-white flex items-center justify-between">
                <h3 className="font-semibold text-[#885ABB]">Service Details</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <Info className="w-5 h-5 text-[#885ABB] shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500">Description</p>
                    <p className="text-sm text-gray-900 mt-1">{service.description || "No description provided."}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Clock className="w-5 h-5 text-[#885ABB] shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500">Time Configuration</p>
                    <p className="text-sm text-gray-900 font-medium mt-1">{service.duration} {service.timeUnit}</p>
                  </div>
                </div>

                {service.locationName && (
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-[#885ABB] shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-gray-500">Location</p>
                      <p className="text-sm text-gray-900 font-medium mt-1">{service.locationName}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Staff */}
          <div className="lg:col-span-2 space-y-6">
            <div className="border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b-2 border-[#885ABB] inline-block">Assigned Specialists</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {service.staff && service.staff.length > 0 ? (
                service.staff.map((member: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-200 hover:border-[#885ABB]/30 transition-all cursor-pointer">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-[#885ABB] font-bold">
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900">{member.name}</h4>
                      <p className="text-xs text-gray-500">Service Specialist</p>
                    </div>
                    <div className="px-2 py-1 bg-purple-50 rounded text-[10px] font-bold text-[#885ABB] uppercase">
                      Active
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 text-center py-12 bg-white rounded-xl border border-dashed border-gray-200">
                  <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900">No Staff Assigned</h3>
                  <p className="text-gray-500">No employees are currently assigned to this service.</p>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
